<script setup>
import { ref, watch, computed, onMounted, onBeforeUnmount, defineProps, defineEmits } from 'vue';

const props = defineProps({
    modelValue: {
        type: Array,
        default: () => []
    },
    options: {
        type: Array,
        required: true
    },
    optionLabel: {
        type: String,
        default: 'label'
    },
    optionValue: {
        type: String,
        default: 'value'
    },
    placeholder: {
        type: String,
        default: 'Select options'
    },
    multiple: {
        type: Boolean,
        default: true
    },
    required: {
        type: Boolean,
        default: false
    },
    disabled: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits(['update:modelValue', 'open']);

const isOpen = ref(false);
const searchQuery = ref('');
const containerRef = ref(null);

const selectedOptions = ref(props.modelValue || []);

watch(() => props.modelValue, (newValue) => {
    selectedOptions.value = newValue;
}, { deep: true });

const selectedLabels = computed(() => {
    return selectedOptions.value.map(selectedOption => {
        const option = props?.options?.find(opt => opt[props?.optionValue] === selectedOption);
        return option ? option[props?.optionLabel] : '';
    });
});

const filteredOptions = computed(() => {
    return props?.options?.filter(option => {
        if (!option || option[props?.optionLabel] === undefined) return false;
        
        const label = option[props?.optionLabel]?.toLowerCase();
        const query = searchQuery.value.toLowerCase();
        const notSelected = !selectedOptions.value.includes(option[props?.optionValue]);
        return label.includes(query) && (props?.multiple ? notSelected : true);
    });
});

const toggleDropdown = () => {
    if (props.disabled) return;
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        searchQuery.value = '';
        emit('open');
    }
};

const selectOption = (option) => {
    if (props.disabled) return;
    const value = option[props.optionValue];
    if (props.multiple) {
        if (!selectedOptions.value.includes(value)) {
            const newSelected = [...selectedOptions.value, value];
            selectedOptions.value = newSelected;
            emit('update:modelValue', newSelected);
        }
    } else {
        selectedOptions.value = [value];
        emit('update:modelValue', [value]);
        isOpen.value = false;
    }
    searchQuery.value = '';
};

const removeOption = (valueToRemove) => {
    if (props.disabled) return;
    const newSelected = selectedOptions.value.filter(value => value !== valueToRemove);
    selectedOptions.value = newSelected;
    emit('update:modelValue', newSelected);
};

onMounted(() => {
    const handleClickOutside = (event) => {
        if (containerRef.value && !containerRef.value.contains(event.target)) {
            isOpen.value = false;
        }
    };
    document.addEventListener('click', handleClickOutside);
    onBeforeUnmount(() => {
        document.removeEventListener('click', handleClickOutside);
    });
});
</script>

<template>
    <div ref="containerRef" class="multiselect">
        <div 
            class="multiselect__select"
            :class="{ 
                'multiselect__select--open': isOpen,
                'multiselect__select--has-value': selectedOptions.length > 0,
                'multiselect__select--disabled': disabled
            }"
            @click="toggleDropdown"
        >
            <div class="multiselect__tags">
                <template v-if="selectedOptions.length">
                    <span 
                        v-for="(value, index) in selectedOptions" 
                        :key="value"
                        class="multiselect__tag"
                        :class="{ 'multiselect__tag--disabled': disabled }"
                    >
                        {{ selectedLabels[index] }}
                        <button 
                            v-if="!disabled"
                            type="button"
                            class="multiselect__tag-remove"
                            @click.stop="removeOption(value)"
                        >
                            ×
                        </button>
                    </span>
                </template>
                <span v-else class="multiselect__placeholder">
                    {{ placeholder }}
                </span>
            </div>
            <div class="multiselect__caret">
                <svg 
                    class="multiselect__caret-icon"
                    :class="{ 
                        'multiselect__caret-icon--open': isOpen,
                        'multiselect__caret-icon--disabled': disabled 
                    }"
                    xmlns="http://www.w3.org/2000/svg" 
                    viewBox="0 0 20 20" 
                    fill="currentColor"
                >
                    <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd" />
                </svg>
            </div>
        </div>

        <div v-show="isOpen && !disabled" class="multiselect__dropdown">
            <input
                type="text"
                class="multiselect__search"
                v-model="searchQuery"
                placeholder="Search..."
                @click.stop
            >
            <div class="multiselect__options">
                <template v-if="filteredOptions?.length">
                    <button
                        v-for="option in filteredOptions"
                        :key="option[optionValue]"
                        type="button"
                        class="multiselect__option"
                        @click="selectOption(option)"
                    >
                        {{ option[optionLabel] }}
                    </button>
                </template>
                <div v-else class="multiselect__no-options">
                    No options available
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="postcss">
.multiselect {
    @apply relative w-full text-white/80
}

.multiselect__select {
    @apply flex items-center justify-between w-full px-4 py-1.5 rounded-[13px]
        bg-echoes-background-black
        text-white/80 hover:text-white
        border border-echoes-border-gray
       
      transition-all duration-200 ease-in-out cursor-pointer
        focus:outline-none focus:ring-2  focus:border-echoes-primary;
    min-height: 2.75rem;
    height: auto;

    &:not(.multiselect__select--open):hover {
        @apply text-white border-white/20
         
    }
}

.multiselect__select--open {
    @apply 
        border border-echoes-primary
        bg-echoes-background-black hover:border-echoes-primary
       
}

.multiselect__tags {
    @apply flex flex-wrap gap-1.5 flex-1 py-1;
    min-height: 1.75rem;
    max-width: calc(100% - 2rem);
}

.multiselect__tag {
    @apply inline-flex items-center gap-1 px-2.5 py-0.5 text-sm rounded-md
        bg-echoes-background-black text-echoes-primary 
        dark:bg-echoes-primary/20 dark:text-white;
}

.multiselect__tag-remove {
    @apply inline-flex items-center justify-center w-4 h-4 rounded-full 
        hover:bg-echoes-background-black/50 dark:hover:bg-echoes-primary/30
        transition-colors duration-150 ease-in-out ml-1;
}

.multiselect__placeholder {
    @apply text-white/50 hover:text-white/80 text-sm flex items-center;
}

.multiselect__caret {
    @apply flex items-center ml-2 flex-shrink-0;
}

.multiselect__caret-icon {
    @apply w-5 h-5 text-white/80 transition-transform duration-200;
}

.multiselect__caret-icon--open {
    @apply transform rotate-180;
}

.multiselect__dropdown {
    @apply z-[9999] w-full mt-1
        bg-echoes-background-black
        border border-echoes-border-gray
        rounded-2xl shadow-lg 
        overflow-hidden
        absolute;
    min-width: 100%;
}

.multiselect__search {
    @apply w-full px-4 py-2 text-sm
        bg-transparent
        text-white
        border-b border-echoes-border-gray
        placeholder-white/80;

    &:focus {
        @apply outline-none;
    }
}

.multiselect__options {
    @apply max-h-32 overflow-y-auto;
    scrollbar-width: thin;
}

.multiselect__option {
    @apply w-full px-4 py-2 text-left text-sm
        text-white
        transition-colors duration-150 ease-in-out;

    &:hover {
        @apply bg-echoes-background-gray/50
    }
}

.multiselect__no-options {
    @apply px-4 py-3 text-sm text-white/80;
}

.multiselect__select--disabled {
    @apply opacity-50 cursor-not-allowed bg-echoes-background-black/50 border-echoes-border-gray;

    &:hover {
        @apply bg-echoes-background-black/50 border-echoes-border-gray shadow-none;
    }
}

.multiselect__tag--disabled {
    @apply opacity-70;
}

.multiselect__caret-icon--disabled {
    @apply opacity-50;
}
</style>